import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vehiclesAPI } from '../../api';
import Layout from '../../components/Layout';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { FormLabel } from '../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import SlideOver from '../../components/ui/SlideOver';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import {
  ArrowLeft,
  Car,
  Edit,
  Plus,
  Trash2,
  Bell,
  BellOff,
  Gauge,
  Wrench,
  Calendar,
} from 'lucide-react';
import VehicleForm from './VehicleForm';
import { useToast } from '../../hooks/useToast';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB');
};

const formatMileage = (val) => {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-GB').format(val);
};

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditSlideOver, setShowEditSlideOver] = useState(false);
  const [showServiceSlideOver, setShowServiceSlideOver] = useState(false);
  const [showReminderSlideOver, setShowReminderSlideOver] = useState(false);
  const [showMileageSlideOver, setShowMileageSlideOver] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState(null);
  const [reminderServiceId, setReminderServiceId] = useState(null);

  const fetchVehicle = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const data = await vehiclesAPI.getOne(id);
      setVehicle(data);
    } catch (err) {
      setError(err.message || 'Failed to load vehicle');
      setVehicle(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVehicle();
  }, [fetchVehicle]);

  const handleEditSuccess = () => {
    setShowEditSlideOver(false);
    fetchVehicle();
    addToast({ type: 'success', message: 'Vehicle updated' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--ds-accent-primary)]"></div>
            <p className="mt-4 text-body text-[var(--ds-text-muted)]">Loading vehicle...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !vehicle) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
            {error || 'Vehicle not found'}
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/tenant/vehicles')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Vehicles
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <TooltipProvider>
      <Layout>
        {/* Header */}
        <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/tenant/vehicles')}
                    className="text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Vehicles
                  </Button>
                </div>
                <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                  {vehicle.reg_number} — {[vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                  {vehicle.year ? ` (${vehicle.year})` : ''}
                </h1>
                <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                  {vehicle.assigned_user_name || vehicle.assigned_user_email || 'Unassigned'} •{' '}
                  {formatMileage(vehicle.current_mileage)} miles
                </p>
              </div>
              <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
                <Button variant="outline" size="sm" onClick={() => setShowEditSlideOver(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Vehicle
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          {/* Vehicle Info Card */}
          <Card className="bg-white dark:bg-gray-800">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="h-14 w-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-300 flex-shrink-0">
                  <Car className="h-7 w-7" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100">
                      {vehicle.reg_number}
                    </h2>
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        vehicle.is_active
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {vehicle.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {[vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' ')}
                    {vehicle.colour ? ` • ${vehicle.colour}` : ''}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Assigned to
                      </FormLabel>
                      <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                        {vehicle.assigned_user_name || vehicle.assigned_user_email || '—'}
                      </p>
                    </div>
                    <div>
                      <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Current Mileage
                      </FormLabel>
                      <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                        {formatMileage(vehicle.current_mileage)}
                      </p>
                    </div>
                    <div>
                      <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        VIN
                      </FormLabel>
                      <p className="mt-1 text-sm text-gray-900 dark:text-gray-100 font-mono">
                        {vehicle.vin || '—'}
                      </p>
                    </div>
                    <div>
                      <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Colour
                      </FormLabel>
                      <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{vehicle.colour || '—'}</p>
                    </div>
                  </div>
                  {vehicle.notes && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <FormLabel className="uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Notes
                      </FormLabel>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {vehicle.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Service Schedule */}
          <Card className="overflow-hidden bg-white dark:bg-gray-800">
            <div className="px-6 py-4 border-b border-[var(--ds-border-divider)] flex items-center justify-between">
              <h3 className="text-h2 font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Service Schedule
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setServiceToEdit(null);
                  setShowServiceSlideOver(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Service
              </Button>
            </div>
            {!vehicle.services || vehicle.services.length === 0 ? (
              <div className="p-8 text-center text-body text-[var(--ds-text-muted)]">
                <p>No services configured yet</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setServiceToEdit(null);
                    setShowServiceSlideOver(true);
                  }}
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Service Type</TableHead>
                    <TableHead>Last Service</TableHead>
                    <TableHead>Next Due (Date)</TableHead>
                    <TableHead>Next Due (Miles)</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicle.services.map((svc) => (
                    <TableRow key={svc.id}>
                      <TableCell>
                        <span className="font-medium text-[var(--ds-text-primary)]">{svc.service_type}</span>
                        {svc.interval_miles && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">
                            Every {formatMileage(svc.interval_miles)} mi
                            {svc.interval_months ? ` / ${svc.interval_months} mo` : ''}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-[var(--ds-text-primary)]">{formatDate(svc.last_service_date)}</span>
                        {svc.last_service_mileage != null && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 block">
                            @ {formatMileage(svc.last_service_mileage)} mi
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-[var(--ds-text-primary)]">
                        {formatDate(svc.next_due_date)}
                      </TableCell>
                      <TableCell className="text-[var(--ds-text-primary)]">
                        {formatMileage(svc.next_due_mileage)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip content="Add reminder">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => {
                                setReminderServiceId(svc.id);
                                setShowReminderSlideOver(true);
                              }}
                              className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                            >
                              <Bell className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => {
                                setServiceToEdit(svc);
                                setShowServiceSlideOver(true);
                              }}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Delete">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={async () => {
                                if (!confirm('Delete this service?')) return;
                                try {
                                  await vehiclesAPI.deleteService(id, svc.id);
                                  addToast({ type: 'success', message: 'Service deleted' });
                                  fetchVehicle();
                                } catch (err) {
                                  addToast({ type: 'error', message: err.message || 'Failed to delete service' });
                                }
                              }}
                              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>

          {/* Reminders */}
          {vehicle.reminders && vehicle.reminders.length > 0 && (
            <Card className="overflow-hidden bg-white dark:bg-gray-800">
              <div className="px-6 py-4 border-b border-[var(--ds-border-divider)] flex items-center justify-between">
                <h3 className="text-h2 font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Pending Reminders
                </h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Service</TableHead>
                    <TableHead>Remind Date</TableHead>
                    <TableHead>Remind by Mileage</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicle.reminders.map((rem) => {
                    const svc = vehicle.services?.find((s) => s.id === rem.service_id);
                    return (
                      <TableRow key={rem.id}>
                        <TableCell className="text-[var(--ds-text-primary)]">
                          {svc?.service_type || '—'}
                        </TableCell>
                        <TableCell>{formatDate(rem.remind_at)}</TableCell>
                        <TableCell>{formatMileage(rem.remind_by_mileage)}</TableCell>
                        <TableCell className="text-right">
                          <Tooltip content="Dismiss reminder">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await vehiclesAPI.dismissReminder(rem.id);
                                  addToast({ type: 'success', message: 'Reminder dismissed' });
                                  fetchVehicle();
                                } catch (err) {
                                  addToast({ type: 'error', message: err.message || 'Failed to dismiss' });
                                }
                              }}
                              className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                            >
                              <BellOff className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}

          {/* Mileage Log */}
          <Card className="overflow-hidden bg-white dark:bg-gray-800">
            <div className="px-6 py-4 border-b border-[var(--ds-border-divider)] flex items-center justify-between">
              <h3 className="text-h2 font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Gauge className="h-5 w-5" />
                Mileage Log
              </h3>
              <Button variant="outline" size="sm" onClick={() => setShowMileageSlideOver(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Log Mileage
              </Button>
            </div>
            {!vehicle.mileageLogs || vehicle.mileageLogs.length === 0 ? (
              <div className="p-8 text-center text-body text-[var(--ds-text-muted)]">
                <p>No mileage entries yet</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowMileageSlideOver(true)}
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Log Mileage
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Start</TableHead>
                    <TableHead className="text-right">End</TableHead>
                    <TableHead className="text-right">Business Miles</TableHead>
                    <TableHead>Logged By</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicle.mileageLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-[var(--ds-text-primary)]">{formatDate(log.logged_at)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMileage(log.start_mileage)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatMileage(log.end_mileage)}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-[var(--ds-text-primary)]">
                        {formatMileage(log.business_miles ?? (log.end_mileage - log.start_mileage))}
                      </TableCell>
                      <TableCell className="text-[var(--ds-text-secondary)]">{log.logged_by_name || '—'}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-[var(--ds-text-secondary)]">
                        {log.notes || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>

        {/* Edit Vehicle SlideOver */}
        <SlideOver
          open={showEditSlideOver}
          onOpenChange={setShowEditSlideOver}
          title="Edit Vehicle"
          width="w-full max-w-2xl"
        >
          <VehicleForm vehicle={vehicle} onClose={() => setShowEditSlideOver(false)} onSuccess={handleEditSuccess} />
        </SlideOver>

        {/* Add/Edit Service SlideOver */}
        <ServiceFormSlideOver
          open={showServiceSlideOver}
          onOpenChange={(open) => {
            setShowServiceSlideOver(open);
            if (!open) setServiceToEdit(null);
          }}
          vehicleId={id}
          service={serviceToEdit}
          currentMileage={vehicle.current_mileage}
          onSuccess={() => {
            setShowServiceSlideOver(false);
            setServiceToEdit(null);
            fetchVehicle();
            addToast({ type: 'success', message: serviceToEdit ? 'Service updated' : 'Service added' });
          }}
          onError={(msg) => addToast({ type: 'error', message: msg })}
        />

        {/* Add Reminder SlideOver */}
        <ReminderFormSlideOver
          open={showReminderSlideOver}
          onOpenChange={(open) => {
            setShowReminderSlideOver(open);
            if (!open) setReminderServiceId(null);
          }}
          vehicleId={id}
          serviceId={reminderServiceId}
          serviceType={vehicle.services?.find((s) => s.id === reminderServiceId)?.service_type}
          currentMileage={vehicle.current_mileage}
          onSuccess={() => {
            setShowReminderSlideOver(false);
            setReminderServiceId(null);
            fetchVehicle();
            addToast({ type: 'success', message: 'Reminder added' });
          }}
          onError={(msg) => addToast({ type: 'error', message: msg })}
        />

        {/* Log Mileage SlideOver */}
        <MileageLogFormSlideOver
          open={showMileageSlideOver}
          onOpenChange={setShowMileageSlideOver}
          vehicleId={id}
          currentMileage={vehicle.current_mileage}
          onSuccess={() => {
            setShowMileageSlideOver(false);
            fetchVehicle();
            addToast({ type: 'success', message: 'Mileage logged' });
          }}
          onError={(msg) => addToast({ type: 'error', message: msg })}
        />
      </Layout>
    </TooltipProvider>
  );
};

// --- Service Form SlideOver ---
const ServiceFormSlideOver = ({
  open,
  onOpenChange,
  vehicleId,
  service,
  currentMileage,
  onSuccess,
  onError,
}) => {
  const isEdit = !!service;
  const [formData, setFormData] = useState({
    service_type: service?.service_type || '',
    interval_miles: service?.interval_miles ?? '',
    interval_months: service?.interval_months ?? '',
    last_service_date: service?.last_service_date
      ? new Date(service.last_service_date).toISOString().slice(0, 10)
      : '',
    last_service_mileage: service?.last_service_mileage ?? currentMileage ?? '',
    next_due_date: service?.next_due_date ? new Date(service.next_due_date).toISOString().slice(0, 10) : '',
    next_due_mileage: service?.next_due_mileage ?? '',
    notes: service?.notes || '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        service_type: service?.service_type || '',
        interval_miles: service?.interval_miles ?? '',
        interval_months: service?.interval_months ?? '',
        last_service_date: service?.last_service_date
          ? new Date(service.last_service_date).toISOString().slice(0, 10)
          : '',
        last_service_mileage: service?.last_service_mileage ?? currentMileage ?? '',
        next_due_date: service?.next_due_date ? new Date(service.next_due_date).toISOString().slice(0, 10) : '',
        next_due_mileage: service?.next_due_mileage ?? '',
        notes: service?.notes || '',
      });
    }
  }, [open, service, currentMileage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        service_type: formData.service_type.trim(),
        interval_miles: formData.interval_miles !== '' ? parseInt(formData.interval_miles, 10) : null,
        interval_months: formData.interval_months !== '' ? parseInt(formData.interval_months, 10) : null,
        last_service_date: formData.last_service_date || null,
        last_service_mileage: formData.last_service_mileage !== '' ? parseInt(formData.last_service_mileage, 10) : null,
        next_due_date: formData.next_due_date || null,
        next_due_mileage: formData.next_due_mileage !== '' ? parseInt(formData.next_due_mileage, 10) : null,
        notes: formData.notes?.trim() || null,
      };
      if (isEdit) {
        await vehiclesAPI.updateService(vehicleId, service.id, payload);
      } else {
        await vehiclesAPI.addService(vehicleId, payload);
      }
      onSuccess();
    } catch (err) {
      onError(err.message || 'Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title={isEdit ? 'Edit Service' : 'Add Service'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <FormLabel required className="text-gray-700 dark:text-gray-300">
            Service Type
          </FormLabel>
          <Input
            type="text"
            name="service_type"
            value={formData.service_type}
            onChange={handleChange}
            required
            placeholder="e.g. Oil change, MOT, Service"
            className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Interval (miles)</FormLabel>
            <Input
              type="number"
              name="interval_miles"
              value={formData.interval_miles}
              onChange={handleChange}
              min={0}
              placeholder="e.g. 12000"
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Interval (months)</FormLabel>
            <Input
              type="number"
              name="interval_months"
              value={formData.interval_months}
              onChange={handleChange}
              min={0}
              placeholder="e.g. 12"
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Last Service Date</FormLabel>
            <Input
              type="date"
              name="last_service_date"
              value={formData.last_service_date}
              onChange={handleChange}
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Last Service Mileage</FormLabel>
            <Input
              type="number"
              name="last_service_mileage"
              value={formData.last_service_mileage}
              onChange={handleChange}
              min={0}
              placeholder="e.g. 40000"
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Next Due Date</FormLabel>
            <Input
              type="date"
              name="next_due_date"
              value={formData.next_due_date}
              onChange={handleChange}
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Next Due Mileage</FormLabel>
            <Input
              type="number"
              name="next_due_mileage"
              value={formData.next_due_mileage}
              onChange={handleChange}
              min={0}
              placeholder="e.g. 52000"
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Notes</FormLabel>
          <Textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={2}
            className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--ds-border-divider)]">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update Service' : 'Add Service'}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
};

// --- Reminder Form SlideOver ---
const ReminderFormSlideOver = ({
  open,
  onOpenChange,
  vehicleId,
  serviceId,
  serviceType,
  currentMileage,
  onSuccess,
  onError,
}) => {
  const [formData, setFormData] = useState({
    remind_at: '',
    remind_by_mileage: currentMileage ? String(currentMileage + 1000) : '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        remind_at: '',
        remind_by_mileage: currentMileage ? String(currentMileage + 1000) : '',
      });
    }
  }, [open, currentMileage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const remindAt = formData.remind_at ? new Date(formData.remind_at).toISOString() : null;
    const remindByMileage = formData.remind_by_mileage !== '' ? parseInt(formData.remind_by_mileage, 10) : null;
    if (!remindAt && remindByMileage == null) {
      onError('Enter either a remind date or remind-by mileage');
      return;
    }
    setLoading(true);
    try {
      await vehiclesAPI.addReminder(vehicleId, serviceId, {
        remind_at: remindAt,
        remind_by_mileage: remindByMileage,
      });
      onSuccess();
    } catch (err) {
      onError(err.message || 'Failed to add reminder');
    } finally {
      setLoading(false);
    }
  };

  if (!serviceId) return null;

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title={`Add Reminder: ${serviceType || 'Service'}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Remind on Date (optional)</FormLabel>
          <Input
            type="date"
            name="remind_at"
            value={formData.remind_at}
            onChange={handleChange}
            className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Remind at Mileage (optional)</FormLabel>
          <Input
            type="number"
            name="remind_by_mileage"
            value={formData.remind_by_mileage}
            onChange={handleChange}
            min={0}
            placeholder={`e.g. ${(currentMileage || 0) + 1000}`}
            className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            At least one of date or mileage is required. Current mileage: {formatMileage(currentMileage)}
          </p>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--ds-border-divider)]">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Adding...' : 'Add Reminder'}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
};

// --- Mileage Log Form SlideOver ---
const MileageLogFormSlideOver = ({ open, onOpenChange, vehicleId, currentMileage, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    logged_at: new Date().toISOString().slice(0, 10),
    start_mileage: '',
    end_mileage: '',
    notes: '',
    update_current_mileage: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        logged_at: new Date().toISOString().slice(0, 10),
        start_mileage: currentMileage ?? '',
        end_mileage: '',
        notes: '',
        update_current_mileage: true,
      });
    }
  }, [open, currentMileage]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const start = parseInt(formData.start_mileage, 10);
    const end = parseInt(formData.end_mileage, 10);
    if (isNaN(start) || isNaN(end) || end < start) {
      onError('End mileage must be greater than or equal to start mileage');
      return;
    }
    setLoading(true);
    try {
      await vehiclesAPI.addMileageLog(vehicleId, {
        logged_at: new Date(formData.logged_at).toISOString(),
        start_mileage: start,
        end_mileage: end,
        notes: formData.notes?.trim() || null,
        update_current_mileage: formData.update_current_mileage,
      });
      onSuccess();
    } catch (err) {
      onError(err.message || 'Failed to log mileage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="Log Mileage">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <FormLabel required className="text-gray-700 dark:text-gray-300">
            Date of Trip
          </FormLabel>
          <Input
            type="date"
            name="logged_at"
            value={formData.logged_at}
            onChange={handleChange}
            required
            className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel required className="text-gray-700 dark:text-gray-300">
              Start Mileage (odometer)
            </FormLabel>
            <Input
              type="number"
              name="start_mileage"
              value={formData.start_mileage}
              onChange={handleChange}
              required
              min={0}
              placeholder="e.g. 45000"
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <FormLabel required className="text-gray-700 dark:text-gray-300">
              End Mileage (odometer)
            </FormLabel>
            <Input
              type="number"
              name="end_mileage"
              value={formData.end_mileage}
              onChange={handleChange}
              required
              min={0}
              placeholder="e.g. 45120"
              className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Business miles for this trip = End − Start (calculated automatically).
        </p>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Notes (optional)</FormLabel>
          <Textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={2}
            className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="update_current_mileage"
            name="update_current_mileage"
            checked={formData.update_current_mileage}
            onChange={handleChange}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <FormLabel htmlFor="update_current_mileage" className="mb-0 text-gray-700 dark:text-gray-300">
            Update vehicle current mileage to end reading
          </FormLabel>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--ds-border-divider)]">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Logging...' : 'Log Mileage'}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
};

export default VehicleDetail;
